// This class implements logic for manipulating the html for our report view.
// The HTML was server side rendered and we're just adding some light UI manipulation.

import { REPORTS_SETTING_KEY } from '../constants'
import { getSafeJSONValue, setSafeJSONValue } from '@app/utils/local-storage'

const MAIN_TABLE_SELECTOR =
  '.report-table-container     > table[data-behaviour="report-table"]'

export default class ReportTable {
  constructor($dom) {
    this.$dom = $dom
    this.reportId = $dom
      .find(MAIN_TABLE_SELECTOR)
      .attr('data-report-template-id')
    this.reportTableElements = this.$dom.find(`${MAIN_TABLE_SELECTOR}`)
    this.settings =
      getSafeJSONValue(`${REPORTS_SETTING_KEY}-${this.reportId}`) || {}
    if (!this.settings.expandedRows) {
      this.initReportSettings()
    }
    this.restoredState = true
    this.adjustResetButton()
  }
  adjustResetButton() {
    const resetButton = document.querySelector(
      `[data-behaviour='report-reset-expand']`
    )

    if (resetButton) {
      if (
        this.settings.expanded ||
        this.settings.collapsed ||
        this.settings.expandedRows.length > 0 ||
        this.settings.collapsedRows.length > 0
      ) {
        resetButton.classList.remove('tw-hidden')
      } else {
        resetButton.classList.add('tw-hidden')
      }
    }
  }

  saveReportSettings() {
    setSafeJSONValue(`${REPORTS_SETTING_KEY}-${this.reportId}`, this.settings)
    this.adjustResetButton()
  }

  initReportSettings() {
    this.settings.expanded = false
    this.settings.collapsed = false
    this.settings.collapsedRows = []
    this.settings.expandedRows = []
  }

  resetReportSettings() {
    this.initReportSettings()
    this.saveReportSettings(this.settings)
    window.location.reload()
  }

  restoreReportSettings() {
    this.restoredState = false
    if (this.settings.expanded) {
      this.expandAll()
    }

    if (this.settings.collapsed) {
      this.collapseAll()
    }

    this.settings.expandedRows.forEach((id) => {
      const row = this.$dom.find(`#report_group_${id}`)
      if (row) {
        this.expandRow(row)
      }
    })

    this.settings.collapsedRows.forEach((id) => {
      const row = this.$dom.find(`#report_group_${id}`)
      if (row) {
        this.collapseRow(row)
      }
    })

    this.restoredState = true
  }

  expandAllInSettings() {
    this.settings.expanded = true
    this.settings.collapsed = false
    this.settings.collapsedRows = []
    this.settings.expandedRows = []
    this.saveReportSettings(this.settings)
  }

  colapseAllInSettings() {
    this.settings.expanded = false
    this.settings.collapsed = true
    this.settings.collapsedRows = []
    this.settings.expandedRows = []
    this.saveReportSettings(this.settings)
  }

  expandRowInSettings(row) {
    const id = this.extractId(row)
    if (this.settings.collapsedRows.includes(id)) {
      this.settings.collapsedRows.splice(
        this.settings.collapsedRows.indexOf(id),
        1
      )
    } else {
      this.settings.expandedRows.push(id)
    }
    this.saveReportSettings(this.settings)
  }

  collapseRowInSettings(row) {
    const id = this.extractId(row)
    if (this.settings.expandedRows.includes(id)) {
      this.settings.expandedRows.splice(
        this.settings.expandedRows.indexOf(id),
        1
      )
    } else {
      this.settings.collapsedRows.push(id)
    }
    this.saveReportSettings(this.settings)
  }

  extractId(row) {
    const match = row.attr('id').match(/^report_group_(\d+)$/)
    return match ? parseInt(match[1], 10) : null
  }

  expandRow(row) {
    row.removeAttr('data-collapsed')

    const currentDataLevel = row.data('level')
    let $item = row.next()

    while ($item.data('level') > currentDataLevel) {
      if ($item.attr('data-collapsed') === 'true') {
        const collapsedGroupLevel = $item.data('level')

        $item.show()
        $item = $item.next()

        // Skip showing all the items inside this collapsed subgroup
        while ($item.data('level') > collapsedGroupLevel) {
          $item = $item.next()
        }
      } else {
        $item.css({ display: 'table-row' }) // much faster than show() on Safari
        $item = $item.next()
      }
    }
    if (this.restoredState) {
      this.expandRowInSettings(row)
    }
  }

  collapseRow(row) {
    row.attr('data-collapsed', true)

    const currentDataLevel = row.data('level')
    let $item = row.next()

    while ($item.data('level') > currentDataLevel) {
      $item.css({ display: 'none' }) // much faster than hide() on Safari
      $item = $item.next()
    }
    if (this.restoredState) {
      this.collapseRowInSettings(row)
    }
  }

  expandAll() {
    this.reportTableElements
      .find('[data-behaviour~="report-group-expand"]')
      .removeAttr('data-collapsed')
      .end()
      .find('tr')
      .css({ display: 'table-row' })
    this._toggleCollapseAllButton(false)
    if (this.restoredState) {
      this.expandAllInSettings()
    }
  }

  collapseAll() {
    this.reportTableElements
      .find('[data-behaviour~="report-group-expand"]')
      .attr('data-collapsed', true)
      .end()
      .find('tr:not(.level_0)')
      .css({ display: 'none' })
    this._toggleCollapseAllButton(true)
    if (this.restoredState) {
      this.colapseAllInSettings()
    }
  }

  _toggleCollapseAllButton(collapsed) {
    this.$dom
      .find('[data-behaviour="report-table-collapse-all"]')
      .toggle(!collapsed)
    this.$dom
      .find('[data-behaviour="report-table-expand-all"]')
      .toggle(collapsed)
  }
}
